import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AirlinesRulesService } from './../../../shared/services/airlines-rules.service';
import { Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { CommonModule } from '@angular/common';
import { AccountingReportService } from '@app/shared/services/accounting-report-service.service';
import { Airlines, Providers } from '@app/shared/interfaces/accounting-report.interface';
import { Agent, AirlineRule, ProviderSupplier, RuleType } from '@app/shared/interfaces/airline-rules.interface';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-airline-rules-definitions',
    imports: [
        ButtonModule,
        InputTextModule,
        DialogModule,
        MultiSelectModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SelectModule,
        TableModule,
        ToggleSwitchModule,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './airline-rules-definitions.component.html',
    styleUrl: './airline-rules-definitions.component.scss'
})
export class AirlineRulesDefinitionsComponent implements OnInit {
    @ViewChild('dt') dt!: Table;
    @ViewChild('globalInput') globalInput!: ElementRef<HTMLInputElement>;
    editMode: boolean = false;
    selectedRuleId: number | null = null;
    dialogVisible: boolean = false;
    airlineRulesForm!: FormGroup;
    airlineCodes: Airlines[] = [];
    airlineRules: AirlineRule[] = [];
    agents: Agent[] = [];
    checked: boolean = false;
    providers: Providers[] = [];
    globalFilterFields: string[] = [
        'airlineCodes',
        'provider.code',
        'agent.name',
        'ruleType',
        'createdBy',
        'createdAt',
        'updatedBy',
        'updatedAt'
    ];
    cols = [
        { field: 'airlineCodes', header: 'Airline Codes' },
        { field: 'provider.code', header: 'Provider' },
        { field: 'agent.name', header: 'Agent' },
        { field: 'providerSupplier', header: 'Provider Supplier' },
        { field: 'ruleType', header: 'Rule Type' },

        { field: 'createdBy', header: 'Created By' },
        { field: 'createdAt', header: 'Created At' },
        { field: 'updatedBy', header: 'Modified By' },
        { field: 'updatedAt', header: 'Modified At' },
        { field: 'active', header: 'Status' }
    ];
    ruleTypeOptions = [
        { label: 'Restricted', value: RuleType.RESTRICTED },
        { label: 'Allowed', value: RuleType.ALLOWED }
    ];
    providerSuppliers: ProviderSupplier[] = [];
    constructor(
        private airlineRulesService: AirlinesRulesService,
        private formBuilder: FormBuilder,
        private accountingReportService: AccountingReportService,
        private messageService: MessageService,
        private cd: ChangeDetectorRef
    ) {}
    ngOnInit(): void {
        this.getAirlineRules();
        this.initilizeForm();
        this.getInitialOptions();
    }

    getAirlineRules() {
        this.airlineRulesService.getAirlineRules().subscribe((rulesData: AirlineRule[]) => {
            this.airlineRules = rulesData;
            this.cd.detectChanges();
        });
    }
    //table functions
    onGlobalSearch(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }
    //dialog functions
    openCreateDialog() {
        this.dialogVisible = true;
        this.editMode = false;
        this.resetForm();
    }
    onChangeToggle(event: any, id: number) {
        const isActive = event;
        if (isActive) {
            this.airlineRulesService.activateAirlineRule(id).subscribe(() => {
                const rule = this.airlineRules.find((r) => r.id === id);
                if (rule) {
                    rule.active = true;
                    this.cd.detectChanges();
                }
            });
        } else {
            this.airlineRulesService.deactivateAirlineRule(id).subscribe(() => {
                const rule = this.airlineRules.find((r) => r.id === id);
                if (rule) {
                    rule.active = false;
                    this.cd.detectChanges();
                }
            });
        }
    }
    openEditDialog(id: number) {
        this.selectedRuleId = id;
        this.dialogVisible = true;
        this.editMode = true;
        this.airlineRulesService.getAirlineRuleById(id).subscribe((ruleData) => {
            this.airlineRulesForm.patchValue({
                airlineCodes: ruleData.airlineCodes,
                providerId: ruleData.provider?.id,
                agentId: ruleData.agent?.id,
                providerSupplierId: ruleData.providerSupplier ? ruleData.providerSupplier.id : null,
                ruleType: ruleData.ruleType as RuleType
            });
            this.cd.detectChanges();
        });
    }
    initilizeForm() {
        this.airlineRulesForm = this.formBuilder.group({
            airlineCodes: this.formBuilder.control<string[] | null>([], { validators: [Validators.required] }),
            providerId: this.formBuilder.control<number | null>(null, { validators: [Validators.required] }),
            agentId: this.formBuilder.control<number | null>(null, { validators: [Validators.required] }),
            providerSupplierId: this.formBuilder.control<number | null>(null),
            ruleType: this.formBuilder.control<RuleType>(RuleType.RESTRICTED, { validators: [Validators.required] })
        });
    }
    resetForm() {
        this.airlineRulesForm.reset();
    }

    getInitialOptions() {
        this.getAirlineCodes();
        this.getAgents();
        this.getProviders();
    }
    cancelAirlineRules() {
        this.dialogVisible = false;
        this.resetForm();
        if (this.globalInput) {
            this.globalInput.nativeElement.value = '';
        }
        this.dt.clear();
    }
    isInvalid(field: string): boolean {
        const control = this.airlineRulesForm.get(field);
        return !!(control && control.invalid && (control.dirty || control.touched));
    }
    //service functions
    getAirlineCodes() {
        this.accountingReportService.getAirlines().subscribe((codes) => {
            this.airlineCodes = codes;
            this.cd.detectChanges();
        });
    }
    getAgents() {
        this.airlineRulesService.getAgents().subscribe((agentsData: Agent[]) => {
            this.agents = agentsData;
            this.cd.detectChanges();
        });
    }
    getProviders() {
        this.accountingReportService.getProviders().subscribe((providersData) => {
            this.providers = providersData;
            this.cd.detectChanges();
        });
    }
    submitAirlineRules() {
        if (this.airlineRulesForm.invalid) {
            this.airlineRulesForm.markAllAsTouched();
            return;
        }
        const formValue = this.airlineRulesForm.value;
        if (this.editMode && this.selectedRuleId) {
            this.airlineRulesService.updateAirlineRule(this.selectedRuleId, formValue).subscribe({
                next: (response) => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Airline rule updated successfully.' });
                    this.cancelAirlineRules();
                    this.getAirlineRules();
                },
                error: (error) => {
                    console.error('Error updating airline rule:', error);
                }
            });
        } else {
            this.airlineRulesService.createAirlineRule(formValue).subscribe({
                next: (response) => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Airline rule created successfully.' });
                    this.cancelAirlineRules();
                    this.getAirlineRules();
                },
                error: (error) => {
                    console.error('Error creating airline rule:', error);
                }
            });
        }
    }

    onProviderChange(providerId: number | null) {
        if (!providerId) {
            this.providerSuppliers = [];
            this.airlineRulesForm.patchValue({ providerSupplierId: null }, { emitEvent: false });
            return;
        }

        this.providerSuppliers = [];

        this.airlineRulesService.getProviderSuppliers(providerId).subscribe({
            next: (res) => {
                this.providerSuppliers = Array.isArray(res) ? res : [res];
                this.cd.detectChanges();
            },
            error: (err) => {
                console.error(err);
                this.providerSuppliers = [];
                this.cd.detectChanges();
            }
        });
    }
}
